import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Lock, Download, Eye, EyeOff, RefreshCw, Copy } from "lucide-react";

export default function DesignVault() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [designs, setDesigns] = useState([]);
  const [showRaw, setShowRaw] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [query, setQuery] = useState("");

  useEffect(() => { if (!loading && (!user || !user.is_admin)) nav("/auth"); }, [user, loading, nav]);

  const load = useCallback(async () => {
    try {
      const params = statusFilter !== "all" ? { status: statusFilter } : {};
      const r = await api.get("/admin/designs", { params });
      setDesigns(r.data.designs || []);
    } catch { toast.error("Failed to load designs"); }
  }, [statusFilter]);

  useEffect(() => { if (user?.is_admin) load(); }, [user, load]);

  const downloadOriginal = async (d) => {
    try {
      // httpOnly cookie is sent automatically with credentials: 'include'
      const url = `${API_BASE}/api/files/${d.design_path}?raw=1`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const filename = (d.name || d.design_id).replace(/[^\w-]+/g, "_") + ".png";
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(dlUrl);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const copySlug = (slug) => {
    navigator.clipboard.writeText(slug || "").then(() => toast.success("Slug copied")).catch(() => {});
  };

  const filtered = designs.filter(d => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (d.name || "").toLowerCase().includes(q) || (d.slug || "").toLowerCase().includes(q) || (d.tags || []).some(t => (t || "").toLowerCase().includes(q));
  });

  // Preview URL — cookie carries admin auth for <img> tags on the same origin.
  const previewUrl = (d) => {
    const base = `${API_BASE}/api/files/${d.design_path}`;
    if (showRaw) return `${base}?raw=1`;
    return base;
  };

  if (!user?.is_admin) return null;

  return (
    <div className="max-w-6xl mx-auto px-6 py-12" data-testid="vault-page">
      <div className="flex items-center gap-3 mb-2">
        <Lock size={22} className="text-[color:var(--hb-royal)]"/>
        <h1 className="font-display text-5xl text-[color:var(--hb-navy)]">Design Vault</h1>
      </div>
      <p className="text-sm text-neutral-600 mb-6">Private admin-only view. Public previews everywhere else on the site are watermarked and downscaled — the vault shows clean originals so you can download the print-ready file with one click.</p>

      <div className="flex flex-wrap gap-3 items-center mb-6">
        <input
          value={query} onChange={e => setQuery(e.target.value)}
          placeholder="Search designs, tags, slug…"
          className="border border-neutral-300 px-3 py-2 text-sm flex-1 min-w-[220px]"
          data-testid="vault-search"
        />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border border-neutral-300 px-3 py-2 text-sm" data-testid="vault-filter">
          <option value="all">All</option>
          <option value="active">Active</option>
          <option value="draft">Draft</option>
          <option value="archived">Archived</option>
        </select>
        <button onClick={() => setShowRaw(v => !v)} className="text-xs uppercase tracking-widest border border-neutral-300 px-3 py-2 flex items-center gap-1.5" data-testid="toggle-raw">
          {showRaw ? <Eye size={12}/> : <EyeOff size={12}/>} {showRaw ? "Clean originals" : "Public preview"}
        </button>
        <button onClick={load} className="text-xs uppercase tracking-widest border border-neutral-300 px-3 py-2 flex items-center gap-1" data-testid="vault-refresh">
          <RefreshCw size={12}/> Refresh
        </button>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-neutral-300 text-sm text-neutral-500" data-testid="vault-empty">
          No designs match your filter.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="vault-grid">
          {filtered.map(d => (
            <div key={d.design_id} className="border border-neutral-200 bg-white overflow-hidden flex flex-col" data-testid={`vault-item-${d.design_id}`}>
              <div className="aspect-square bg-[color:var(--hb-warm)] relative flex items-center justify-center">
                {d.design_path
                  ? <img src={previewUrl(d)} alt={d.name} className="w-full h-full object-contain"/>
                  : <div className="text-xs text-neutral-400">No image</div>}
                {!showRaw && (
                  <div className="absolute bottom-1.5 right-1.5 text-[9px] bg-black/70 text-white px-1.5 py-0.5 uppercase tracking-widest">Watermarked</div>
                )}
              </div>
              <div className="p-3 flex-1 flex flex-col">
                <div className="text-sm font-medium truncate" title={d.name}>{d.name || d.design_id}</div>
                <div className="text-[10px] text-neutral-500 mt-0.5 flex items-center gap-1">
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${d.status === "active" ? "bg-green-500" : d.status === "draft" ? "bg-amber-500" : "bg-neutral-400"}`}/>
                  {d.status || "unknown"}
                  {d.ai_grade?.grade && <span className="ml-2">Grade: <b>{d.ai_grade.grade}</b></span>}
                </div>
                {d.slug && (
                  <button onClick={() => copySlug(d.slug)} className="mt-1 text-[10px] text-neutral-500 flex items-center gap-1 hover:text-[color:var(--hb-royal)]" data-testid={`copy-slug-${d.design_id}`}>
                    <Copy size={9}/> <span className="truncate">{d.slug}</span>
                  </button>
                )}
                <div className="mt-auto pt-3">
                  <button onClick={() => downloadOriginal(d)} className="w-full text-xs uppercase tracking-widest bg-[color:var(--hb-navy)] text-white py-1.5 flex items-center justify-center gap-1.5" data-testid={`download-${d.design_id}`}>
                    <Download size={11}/> Download original
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-8 flex gap-3 text-xs">
        <Link to="/admin" className="text-[color:var(--hb-royal)] uppercase tracking-widest" data-testid="back-to-admin">← Admin home</Link>
      </div>
    </div>
  );
}
